import React, { useState, useImperativeHandle, forwardRef } from 'react';
import { Modal, Table, Button, Tooltip, Tag, Checkbox } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FileTextOutlined } from '@ant-design/icons';
import { makePostCall, getUserDetails, hasReportingPermission, sortedModalityOptionsNew, } from '../../utils/helper';
import { getStatusStyle, getReportIconTone, REPORT_TONE_LABEL } from '../../utils/sharedTableColumns';
import {
  isCancelledStudy,
  isUnconfirmedReportingBlocked,
  openReport as openReportOnly,
} from '../../utils/tableActionHandlers';
import { getPacsStatusLabel } from '../../constants/fieldLabels';
import { ReportIcon, ViewerIcon, ACTION_HOVER } from '../ActionIcons';

export interface PatientOrdersModalRef {
  open: (record: any) => void;
}

interface ModalState {
  visible: boolean;
  patientName: string;
  patPin: string;
  orders: any[];
  loading: boolean;
}

const INITIAL_STATE: ModalState = {
  visible: false,
  patientName: '',
  patPin: '',
  orders: [],
  loading: false,
};

interface Props {
  onOpenReport: (pseudoRecord: any) => void;
  onOpenViewer: (pseudoRecord: any) => void;
  onViewReport: (pseudoRecord: any) => void;
  /**
   * Opens the report editor for a prior order (the PACS list's openReport). Optional —
   * when omitted (Dispatch / Front Desk) the patient name is plain text and the Open
   * Reporting column is hidden.
   */
  onOpenReporting?: (pseudoRecord: any) => void;
}

/**
 * Re-shape an order-rooted history row into the study-rooted record the PACS list's
 * handlers expect (pacs_order.* / order_workflow / top-level ps_study_uid), so the
 * reporting + viewer guards (cancelled, unconfirmed, centre proxy) behave identically.
 */
const toStudyRecord = (rec: any) => ({
  ps_study_uid: rec?.pacs_study?.ps_study_uid,
  oo_doc_id: rec?.oo_doc_id,
  order_workflow: rec?.order_workflow,
  pacs_order: {
    pacs_ord_id: rec?.pacs_ord_id,
    po_his_status: rec?.po_his_status,
    po_center_code: rec?.po_center_code,
    po_modality: rec?.po_modality,
    po_body_part: rec?.po_body_part,
    po_diag_desc: rec?.po_diag_desc,
    po_acc_no: rec?.po_acc_no,
    patient: rec?.patient,
  },
});

// Reporting is impossible for these — mirrors the PACS list's report-icon column.
// History rows are order-rooted, so check po_his_status at the top level too.
const isUnreportable = (rec: any) =>
  isCancelledStudy(rec) ||
  rec?.po_his_status === 'CANCELLED' ||
  rec?.order_workflow?.ow_pacs_status === 'NOT_FOR_REPORTING';

// Same icon box + hover tint as the PACS table's leading action columns
// (sharedTableColumns buildLeadingActionColumns: spanBase + hoverVar).
const spanBase: React.CSSProperties = { padding: '1px', width: '24px', height: '24px' };
const hoverVar = (key: string): React.CSSProperties =>
  ({ ['--ai-hover' as any]: ACTION_HOVER[key] } as React.CSSProperties);

// Slim headerless icon column, like the PACS table's 26px slivers. antd pads cells
// 8px each side, which would crush a 26px column, so trim it on these cells only.
const iconColumn = (key: string, render: (_: any, rec: any) => React.ReactNode) => ({
  key,
  title: '',
  width: 30,
  align: 'center' as const,
  onCell: () => ({ style: { padding: '4px 2px' } }),
  onHeaderCell: () => ({ style: { padding: '4px 2px' } }),
  render,
});

const buildColumns = (
  onOpenReport: Props['onOpenReport'],
  onOpenViewer: Props['onOpenViewer'],
  onViewReport: Props['onViewReport'],
  onOpenReporting: Props['onOpenReporting'],
  // Report icon: reporting WITHOUT the viewer, like the PACS list's report icon.
  onOpenReportingOnly: (pseudoRecord: any) => void,
): ColumnsType<any> => {
  // Same rule as the PACS patient-name cell: technicians open the viewer, everyone
  // else opens reporting (only when the host page supports reporting at all).
  const isTechnician = getUserDetails()?.user_type === 'technician';
  const canReport = !!onOpenReporting && !isTechnician && hasReportingPermission(getUserDetails());
  const nameClickable = (isTechnician && !!onOpenReporting) || canReport;

  const openViewerFor = (rec: any) => onOpenViewer(toStudyRecord(rec));

  return [
  {
    title: 'S.No',
    width: 50,
    render: (_: any, __: any, index: number) => index + 1,
  },
  ...(canReport
    ? [
        iconColumn('report_icon', (_: any, rec: any) => {
          // Same rules as the PACS report icon: dropped for cancelled /
          // not-for-reporting, disabled when unconfirmed reporting is blocked.
          if (isUnreportable(rec)) return null;
          const hasDicom = !!rec?.pacs_study?.ps_study_uid;
          const isReportingBlocked = rec?.order_workflow?.ow_reporting_blocked === true;
          const isUnconfirmed = rec?.po_his_status === 'UNCONFIRMED';
          const unconfirmedBlocked = isUnconfirmed && isUnconfirmedReportingBlocked();
          const isBlocked = !hasDicom || isReportingBlocked || unconfirmedBlocked;
          const tone = getReportIconTone(rec?.order_workflow?.ow_pacs_status);
          const title = !hasDicom
            ? 'No DICOM study'
            : isReportingBlocked
              ? 'Reporting blocked'
              : unconfirmedBlocked
                ? 'Cannot report unconfirmed study'
                : isUnconfirmed
                  ? `${REPORT_TONE_LABEL[tone]} — study not confirmed in HIS`
                  : REPORT_TONE_LABEL[tone];
          return (
            <Tooltip title={title}>
              <span
                className={`action-icon-button ${isBlocked ? 'disabled' : ''}`}
                style={{ ...spanBase, ...(isBlocked ? {} : hoverVar('report')) }}
                onClick={e => {
                  e.stopPropagation();
                  if (!isBlocked) onOpenReportingOnly(toStudyRecord(rec));
                }}
              >
                <ReportIcon size={22} disabled={isBlocked} tone={tone} />
              </span>
            </Tooltip>
          );
        }),
      ]
    : []),
  iconColumn('viewer_icon', (_: any, rec: any) => {
    const hasDicom = !!rec?.pacs_study?.ps_study_uid;
    return (
      <Tooltip title={hasDicom ? 'Open Viewer' : 'No DICOM study'}>
        <span
          className={`action-icon-button ${hasDicom ? '' : 'disabled'}`}
          style={{ ...spanBase, ...(hasDicom ? hoverVar('viewer') : {}) }}
          onClick={e => {
            e.stopPropagation();
            if (hasDicom) openViewerFor(rec);
          }}
        >
          <ViewerIcon size={22} disabled={!hasDicom} />
        </span>
      </Tooltip>
    );
  }),
  {
    title: 'Patient Name',
    width: 200,
    ellipsis: true,
    render: (_: any, rec: any) => {
      const name = rec?.patient?.pat_name || '-';
      if (!nameClickable) return name;
      const hasDicom = !!rec?.pacs_study?.ps_study_uid;
      // Technician: viewer needs images. Reporter: study must be scanned, reportable
      // and not blocked — otherwise render muted, like a blocked row in the PACS list.
      const disabled = isTechnician
        ? !hasDicom
        : !hasDicom || isUnreportable(rec) || rec?.order_workflow?.ow_reporting_blocked === true;
      return (
        <Tooltip title={name}>
          <Button
            type="link"
            disabled={disabled}
            onClick={e => {
              e.stopPropagation();
              if (isTechnician) openViewerFor(rec);
              else onOpenReporting!(toStudyRecord(rec));
            }}
            style={{ padding: 0, height: 'auto', maxWidth: '100%' }}
          >
            <span
              style={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textAlign: 'left',
                color: disabled ? '#b0b0b0' : '#2563EB',
                fontWeight: 700,
              }}
            >
              {name}
            </span>
          </Button>
        </Tooltip>
      );
    },
  },
  {
    title: 'Study Description',
    width: 180,
    ellipsis: true,
    render: (_: any, rec: any) => rec?.po_diag_desc || '-',
  },
  {
    key: 'modality',
    title: 'Modality',
    width: 100,
    render: (_: any, rec: any) => rec?.po_modality || '-',
  },
  {
    title: 'Status',
    // Fits the shared badge's widest label ("UNREPORTED").
    width: 124,
    render: (_: any, rec: any) => {
      const status: string = rec?.order_workflow?.ow_pacs_status;
      if (!status) return <span>-</span>;
      const isSignedoff = status==='SIGNEDOFF';
      return (
          <Tag
            style={{...getStatusStyle(status),cursor: isSignedoff ? 'pointer' : 'default',}}
            onClick={(e) => {
              if (isSignedoff){
                e.stopPropagation();
                onViewReport(rec)
                };
            }}
          >
            {getPacsStatusLabel(status)}
          </Tag>
      );
    },
  },
  {
    title: 'Scan Date/Time',
    width: 155,
    ellipsis: true,
    render: (_: any, rec: any) =>
      rec?.pacs_study?.ps_study_dt_tm ? (
        new Date(rec.pacs_study.ps_study_dt_tm).toLocaleString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      ) : (
        <span style={{ color: '#b0b0b0' }}>Not scanned</span>
      ),
  },
  {
    title: 'View Report',
    width: 60,
    render: (_: any, rec: any) => {
      const hasDicom = !!rec?.pacs_study?.ps_study_uid;
      const isSignedOff = rec?.order_workflow?.ow_pacs_status === 'SIGNEDOFF';
      const disabled = !hasDicom || !isSignedOff;
      return (
        <Tooltip title={disabled ? (hasDicom ? 'Not signed off yet' : 'No DICOM study') : 'Open Report'}>
          <Button
            type="text"
            icon={
              <FileTextOutlined style={{ fontSize: '18px', color: disabled ? '#ccc' : '#1890ff' }} />
            }
            disabled={disabled}
            onClick={() => {
              onOpenReport({
                // viewReport() reads oo_doc_id at the top level to fetch the PDF —
                // forward it from the order so signed-off prior reports open.
                oo_doc_id: rec.oo_doc_id,
                pacs_order: {
                  pacs_ord_id: rec.pacs_ord_id,
                  patient: {
                    pat_pacs_id: rec.patient?.pat_pacs_id,
                    pat_pin: rec.patient?.pat_pin,
                  },
                },
                isFromPatientOrders: true,
              });
            }}
          />
        </Tooltip>
      );
    },
  },
  ];
};

const PatientOrdersModal = forwardRef<PatientOrdersModalRef, Props>(
  ({ onOpenReport, onOpenViewer, onViewReport, onOpenReporting }, ref) => {
    const [modalState, setModalState] = useState<ModalState>(INITIAL_STATE);
    const [modalityFilter, setModalityFilter] = useState<string[] | null>(null);

    useImperativeHandle(ref, () => ({
      open: async (record: any) => {
        // Study-rooted rows (PACS list) nest the patient under pacs_order;
        // order-rooted rows (Dispatch / Consultant list) carry it at the top level.
        const patient = record?.pacs_order?.patient ?? record?.patient;
        const patPin = patient?.pat_pin;
        const patientName = patient?.pat_name || '';
        // The row that opened the popup is already on screen — list only the others.
        const currentOrdId = record?.pacs_order?.pacs_ord_id ?? record?.pacs_ord_id;
        if (!patPin) return;

        setModalState({ visible: true, patientName, patPin, orders: [], loading: true });
        setModalityFilter(null);

        try {
          // /patient-order-history starts from pacs_orders (complete list) and LEFT JOINs
          // order_workflow + pacs_studies — so ALL orders are returned regardless of age or scan status
          const res = await makePostCall('/patient-order-history', {
            pat_pin: patPin,
            exclude_ord_id: currentOrdId,
          });
          setModalState(prev => ({ ...prev, orders: res.data?.data || [], loading: false }));
        } catch (e) {
          console.error('Failed to fetch patient orders', e);
          setModalState(prev => ({ ...prev, orders: [], loading: false }));
        }
      },
    }));

    const handleClose = () => {
      setModalState(INITIAL_STATE);
      setModalityFilter(null);
    };
    // Only offer modalities the patient actually has in the same order as the list filters.
    const modalityFilters = React.useMemo(() => {
      const present = [...new Set(modalState.orders.map(o => o?.po_modality).filter(Boolean))];
      return sortedModalityOptionsNew(present.map(m => ({ label: m, value: m }))).map(o => ({
        text: o.label,
        value: o.value,
      }));
    }, [modalState.orders]);

    // Opening the viewer / reporting (or both) navigates away — close the popup first so
    // returning to the tab lands on the PACS list, not a stale Prior Orders dialog.
    const columns = React.useMemo(() => {
      const openViewerAndClose = (rec: any) => {
        handleClose();
        onOpenViewer(rec);
      };
      const openReportingAndClose = onOpenReporting
        ? (rec: any) => {
            handleClose();
            onOpenReporting(rec);
          }
        : undefined;
      // Same call the PACS report icon makes: disableViewer=true → openviewer=disable.
      const openReportingOnlyAndClose = (rec: any) => {
        handleClose();
        openReportOnly(rec, getUserDetails(), true);
      };
      return buildColumns(
        onOpenReport,
        openViewerAndClose,
        onViewReport,
        openReportingAndClose,
        openReportingOnlyAndClose,
      ).map(col =>
        col.key === 'modality' && modalityFilters.length > 0
          ? {
              ...col,
              // Custom dropdown with no OK / Reset footer: each tick applies immediately.
              filterDropdown: () => (
                <div style={{ padding: '4px 8px' }}>
                  <Checkbox.Group
                    style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
                    options={modalityFilters.map(f => ({ label: f.text, value: f.value }))}
                    value={modalityFilter ?? []}
                    onChange={vals => setModalityFilter(vals.length ? (vals as string[]) : null)}
                  />
                  <Button
                    type="link"
                    size="small"
                    disabled={!modalityFilter?.length}
                    onClick={() => setModalityFilter(null)}
                    style={{ padding: 0, marginTop: 6, height: 'auto' }}
                  >
                    Clear
                  </Button>
                </div>
              ),
              filteredValue: modalityFilter,
              onFilter: (value, rec) => rec?.po_modality === value,
            }
          : col,
      );
    }, [onOpenReport, onOpenViewer, onViewReport, onOpenReporting, modalityFilters, modalityFilter]);

    return (
      <Modal
        title={
          <span>
            Prior Orders for <strong>{modalState.patientName}</strong>
            {modalState.patPin && (
              <span style={{ fontWeight: 400, color: '#888', marginLeft: 8, fontSize: 13 }}>
                (YH No.: {modalState.patPin})
              </span>
            )}
          </span>
        }
        open={modalState.visible}
        onCancel={handleClose}
        footer={null}
        width={1500}
        style={{ top: 35 }}
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      >
        <Table
          className="patient-orders-table"
          size="small"
          loading={modalState.loading}
          dataSource={modalState.orders}
          rowKey={rec => rec?.pacs_ord_id || Math.random()}
          style={{ boxShadow: 'none' }}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          columns={columns}
          onChange={(_, filters) =>
            setModalityFilter((filters.modality as string[] | null) ?? null)
          }
          locale={{ emptyText: 'No prior orders found for this patient' }}
        />
      </Modal>
    );
  },
);

PatientOrdersModal.displayName = 'PatientOrdersModal';

export default PatientOrdersModal;
